import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { BigButton } from '../components/BigButton';
import { AnswerPlacementEditor } from '../components/AnswerPlacementEditor';
import { useWorksheet } from '../context/WorksheetContext';
import { renderWorksheet } from '../lib/render';
import { canvasToJpegDataUrl, canvasToPdfBlob, downloadPdf, downloadPng } from '../lib/export';
import { telemetry } from '../lib/telemetry';
import { getUserName } from '../lib/profile';
import { useAuth } from '../context/AuthContext';
import {
  buildWorksheetTitle,
  saveWorksheet,
  updateWorksheet,
} from '../lib/worksheets';

// Step 4: render the final worksheet with all answers in place, save to the local
// library, preview it, export as PDF or PNG.
//
// "Move answers" switches the preview into the drag-and-resize editor. Placements
// live in WorksheetContext, so when the pupil taps Done the preview re-renders and
// the saved library copy is updated with the new image.
//
// On entry we automatically save the worksheet to IndexedDB so the user can find it
// again from History. The record is created ONCE per visit and updated after edits.
// The exported PDF is attached to the saved record when the user taps Save as PDF.
export function ExportScreen() {
  const navigate = useNavigate();
  const { state, reset, setPlacement, clearPlacement } = useWorksheet();
  const { recordWorksheetUse, isAdmin, subscription } = useAuth();
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [busy, setBusy] = useState<'pdf' | 'png' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const savedIdRef = useRef<string | null>(null);
  const usageRecordedRef = useRef(false);

  useEffect(() => {
    if (!state.image || !state.analysis) {
      navigate('/app', { replace: true });
      return;
    }
    if (editing) return;

    let cancelled = false;
    (async () => {
      try {
        const c = await renderWorksheet(state.image!, state.analysis!.questions, state.answers, state.placements);
        if (cancelled) return;
        setCanvas(c);
        const container = canvasContainerRef.current;
        if (container) {
          container.replaceChildren(c);
          c.style.maxWidth = '100%';
          c.style.height = 'auto';
          c.style.display = 'block';
          c.style.borderRadius = '12px';
        }

        // Save to the local library on first render of this screen. Later renders
        // (after the pupil moves answers) update the same record's image.
        if (savedIdRef.current) {
          const jpegDataUrl = canvasToJpegDataUrl(c);
          const imageBlob = await (await fetch(jpegDataUrl)).blob();
          await updateWorksheet(savedIdRef.current, { imageBlob });
        } else {
          const userName = getUserName();
          const subjectLabel = state.analysis?.subjectLabel ?? 'Worksheet';
          const subject = (state.analysis?.subject ?? 'other').toLowerCase();
          const yearLevel = state.analysis?.yearLevel;
          const capturedAt = Date.now();
          const title = state.analysis?.suggestedTitle
            ? `${state.analysis.suggestedTitle} - ${buildWorksheetTitle({ userName, subjectLabel, yearLevel, capturedAt })}`
            : buildWorksheetTitle({ userName, subjectLabel, yearLevel, capturedAt });

          const jpegDataUrl = canvasToJpegDataUrl(c);
          const imageBlob = await (await fetch(jpegDataUrl)).blob();

          const id = await saveWorksheet({
            title,
            subject,
            subjectLabel,
            yearLevel,
            userName: userName || undefined,
            capturedAt,
            imageBlob,
            questionsCount: state.analysis!.questions.length,
            answersCount: Object.values(state.answers).filter((a) => a.trim()).length,
          });
          savedIdRef.current = id;
          if (!cancelled) setSavedId(id);
        }

        // Count this as a worksheet use against the user's free-use allowance.
        // Skipped for admins and for users with an active subscription (they have
        // unlimited use). We only record once per visit to this screen.
        const hasActiveSub =
          subscription &&
          (subscription.status === 'active' || subscription.status === 'trialing');
        if (!isAdmin && !hasActiveSub && !usageRecordedRef.current) {
          usageRecordedRef.current = true;
          await recordWorksheetUse();
        }

        telemetry.track('worksheet_review_shown', { stage: 'export' });
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message || 'Could not render the worksheet.');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.image, state.analysis, state.answers, state.placements, editing, navigate]);

  async function handlePdf() {
    if (!canvas) return;
    setError(null);
    setBusy('pdf');
    try {
      const blob = canvasToPdfBlob(canvas);
      await downloadPdf(canvas, buildFilename('pdf', state.analysis?.suggestedTitle));
      telemetry.track('worksheet_exported', { format: 'pdf' });
      // Attach the PDF blob to the saved record so the user can re-download from History.
      if (savedId) {
        await updateWorksheet(savedId, { pdfBlob: blob, downloadedAt: Date.now() });
      }
    } catch (e) {
      setError((e as Error).message || 'PDF export failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handlePng() {
    if (!canvas) return;
    setError(null);
    setBusy('png');
    try {
      await downloadPng(canvas, buildFilename('png', state.analysis?.suggestedTitle));
      telemetry.track('worksheet_exported', { format: 'png' });
      if (savedId) {
        await updateWorksheet(savedId, { downloadedAt: Date.now() });
      }
    } catch (e) {
      setError((e as Error).message || 'PNG export failed.');
    } finally {
      setBusy(null);
    }
  }

  function startOver() {
    reset();
    navigate('/app');
  }

  if (!state.image) return null;

  return (
    <Screen>
      <div className="flex-1 flex flex-col gap-5 py-6">
        <header>
          <h2 className="text-2xl font-semibold">{editing ? 'Move your answers' : 'Looks good?'}</h2>
          {!editing && (
            <p className="text-ink/60 text-sm mt-1">
              Saved to your library. If an answer is in the wrong spot, tap Move answers. Then save as a PDF or
              image to send it back to school.
            </p>
          )}
        </header>

        {editing ? (
          <AnswerPlacementEditor
            image={state.image}
            questions={state.analysis?.questions ?? []}
            answers={state.answers}
            placements={state.placements}
            onPlace={setPlacement}
            onReset={clearPlacement}
          />
        ) : (
          <div className="bg-white rounded-2xl border border-ink/10 p-2 shadow-sm">
            <div ref={canvasContainerRef} className="overflow-auto max-h-[55vh]" />
          </div>
        )}

        {savedId && (
          <p className="text-xs text-accent inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            Saved to History
          </p>
        )}

        {error && (
          <div
            role="alert"
            className="bg-warm border border-amber-300 text-ink rounded-xl p-4 text-sm leading-relaxed"
          >
            {error}
          </div>
        )}

        {editing ? (
          <BigButton onClick={() => setEditing(false)}>Done</BigButton>
        ) : (
        <div className="flex flex-col gap-3">
          <BigButton variant="secondary" onClick={() => setEditing(true)} disabled={busy !== null}>
            Move answers
          </BigButton>
          <BigButton onClick={handlePdf} disabled={busy !== null}>
            {busy === 'pdf' ? 'Saving PDF...' : 'Save as PDF'}
          </BigButton>
          <BigButton variant="secondary" onClick={handlePng} disabled={busy !== null}>
            {busy === 'png' ? 'Saving image...' : 'Save as image'}
          </BigButton>
          <button
            onClick={() => navigate('/app/history')}
            className="text-center text-sm text-accent hover:underline py-2"
          >
            See all saved worksheets
          </button>
          <button
            onClick={startOver}
            className="text-center text-sm text-ink/50 hover:text-ink py-2"
          >
            Start a new worksheet
          </button>
        </div>
        )}
      </div>
    </Screen>
  );
}

function buildFilename(ext: 'pdf' | 'png', suggestedTitle?: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const safe = (suggestedTitle ?? 'worksheet').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return `${safe || 'worksheet'}-${stamp}.${ext}`;
}
